import { EditorQueries, NodeTree } from '@candulabs/craft';
import invariant from 'invariant';

import { isRootNode } from 'craft/constants';

import { InsertMode } from './types';

interface NodeInsertLocation {
  parentNodeId: string;
  insertIndex: number;
}

const getInsertMode = (
  nodeTreesToPaste: NodeTree[],
  insertToNodeId: string,
  insertMode: InsertMode = InsertMode.AUTO,
  query: EditorQueries,
): InsertMode => {
  if (insertMode === InsertMode.INTO || insertMode === InsertMode.NEXT_TO) {
    return insertMode;
  }

  const destinationNode = query.node(insertToNodeId);

  if (!destinationNode?.node?.data) {
    return InsertMode.NEXT_TO;
  }

  // first, we check the insertToNodeId. If it's not a canvas, then it's gotta be a NEXT_TO
  const isDestinationNodeCanvas = destinationNode.isCanvas();
  const isDestinationNodeEmptyCanvas = destinationNode.childNodes().length === 0;

  const isSourceNodeCanvas =
    nodeTreesToPaste.findIndex((tree) => tree.nodes[tree.rootNodeId]?.data?.isCanvas) > -1;

  // if the source is an atomic node, we try to add INTO if the destination is a canvas
  if (!isSourceNodeCanvas) {
    return isDestinationNodeCanvas ? InsertMode.INTO : InsertMode.NEXT_TO;
  }

  // else if the source is not atomic node, we always append NEXT_TO
  if (!isDestinationNodeCanvas) {
    return InsertMode.NEXT_TO;
  }

  // else if source is a complex (non empty) canvas node, and the destination is an empty canvas, resolves to INTO, otherwise it resolves to NEXT_TO
  const isSourceNodeEmptyCanvas =
    nodeTreesToPaste.findIndex((tree) => tree.nodes[tree.rootNodeId]?.data?.nodes.length === 0) >
    -1;

  return isDestinationNodeEmptyCanvas && !isSourceNodeEmptyCanvas
    ? InsertMode.INTO
    : InsertMode.NEXT_TO;
};

export const findNodeInsertLocation = (
  nodesToPaste: NodeTree[],
  insertToNodeId: string,
  insertMode: InsertMode = InsertMode.AUTO,
  query: EditorQueries,
): NodeInsertLocation | undefined => {
  const resolvedInsertMode = getInsertMode(nodesToPaste, insertToNodeId, insertMode, query);

  // sanity check...
  invariant(
    resolvedInsertMode === InsertMode.INTO || resolvedInsertMode === InsertMode.NEXT_TO,
    'resolved insert mode can only be into or next to ',
  );
  const isCanvasNode = (nodeId: string) => query.node(nodeId)?.isCanvas();
  if (
    // root nodes can only be inserted into
    isRootNode(insertToNodeId) ||
    (resolvedInsertMode === InsertMode.INTO && isCanvasNode(insertToNodeId))
  ) {
    return {
      parentNodeId: insertToNodeId,
      // insert at the end.
      insertIndex: query.node(insertToNodeId).node?.data?.nodes?.length || 0,
    };
  }

  // you tried to insert into, but the node wasn't a canvas. In this case, we just bail.
  if (resolvedInsertMode === InsertMode.INTO) {
    return undefined;
  }

  const parentNodeId = query.node(insertToNodeId)?.getParent()?.node?.id;
  if (!isCanvasNode(parentNodeId) || !parentNodeId) {
    return undefined;
  }

  const insertToNodeOffset = (query.node(parentNodeId).get()?.data?.nodes || []).indexOf(
    insertToNodeId,
  );
  return { parentNodeId, insertIndex: (insertToNodeOffset ?? -1) + 1 };
};
